import React, { useState, useEffect, useMemo } from 'react';
import { studioTabs, dataTabs, jobsTabs, TabIcons, adminTabs, otherTabs, Tabs, allTabs } from './enums';
import { useDidUpdate } from 'rooks';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import './styles.scss';
import { Hambuger } from './icons';
import classNames from 'classnames';
import { RootState } from '../../store/types';
import { toggleTreeview, closeActiveForm, closeActiveComponentNotes } from '../../store/workflow';
import { toggleWorkflowsInAnalytics } from '../../store/analytics';
import { TooltipRight } from '../tooltips';
import { NetworkConnectionError } from '../error-page';
import { toggleConnectionsTreeview, toggleAdministratorTreeview, setFileBrowserConnectionList } from '../../store/datasources';
import { MainLoadingSkeleton } from '../loading/main-loading-skeleton';
import { ShowWhenTrue } from '../../helpers';
import { getAllClusters, getDataBricksVersionList, setWorkspaceList } from '../../store/cluster';
import { Env } from '../../constants/settings';
import { UserHandler } from '@api/user-handler';
import { setActiveSessionsInfo, setEnvVariables, setPathInfo } from '@store/account';
import { CoachMark, ICoachProps } from "react-coach-mark";
import { FileHandler } from '@api/file-handler';
import { WorkSpaceHandler } from '@api/workSpace-handler';
import { Root } from 'remark-gfm';
import { FaTools } from 'react-icons/fa';

const TIMER_GET_CLUSTERS = 15000;

export const SideBars: React.FC = () => {
    const [activeTab, setActiveTab] = useState({ name: '', url: '' });
    const [isDuplicateInstance, setIsDuplicateInstance] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const isDrillingDataExplorer = location.pathname.includes('data-explorer-drilling-app');
    const { goToAnalyticsPage, initialPageLoadSpinner: showInitialPageLoadSpinner } = useSelector((store: RootState) => store.CommonReducer);
    const { activeUserInfo: userInfo } = useSelector((store: RootState) => store.AccountReducer);
    const { isComponentTreeviewMinimized, activeComponentPropertiesInfo } = useSelector((store: RootState) => store.WorkflowReducer);
    const { envVariables: Env } = useSelector((store:RootState)=> store.AccountReducer);
    const [dataTabsConfig, setDataTabsConfig] = useState(dataTabs(true));
    const [jobsTabsConfig, setJobsTabsConfig] = useState(jobsTabs(false));

    useEffect(() => {
        if (Env?.ENABLE_DATA_EXPLORER !== undefined) {
            setDataTabsConfig(dataTabs(Env.ENABLE_DATA_EXPLORER));
            setActiveTabOnPageLoad();
        }
    }, [Env?.ENABLE_DATA_EXPLORER]);

    useEffect(() => {
        setJobsTabsConfig(jobsTabs(Env?.IS_AIRFLOW_BETA_VISIBLE));
    }, [Env?.IS_AIRFLOW_BETA_VISIBLE]);

    useEffect(() => {
        const globalWindow = window as typeof window & { __deepiqSidebarMounted?: boolean };
        if (globalWindow.__deepiqSidebarMounted) {
            setIsDuplicateInstance(true);
            return;
        }
        globalWindow.__deepiqSidebarMounted = true;
        return () => {
            globalWindow.__deepiqSidebarMounted = false;
        };
    }, []);

    const setActiveTabOnPageLoad = () => {
        const availableTabs = Env?.ENABLE_DATA_EXPLORER === false 
        ? allTabs.filter(tab => !["/dataexplorer", "/dataexplorer-v2"].includes(tab.url))
        : allTabs;
        availableTabs.forEach(tab => {
            if (history.location.pathname.includes(tab.url)) {
                setActiveTab(tab);
                return;
            }
        });
    };

    const dispatchAllClusters = () => {
        dispatch(getAllClusters());
    };

    const dispatchUserActiveSessions = () =>{
        UserHandler.GetActiveSessions(`${Env?.REACT_APP_PLATFORM_URL}/usermanagement/api`, Env?.KEYCLOAK_CLIENT_ID,(response) => {
            dispatch(setActiveSessionsInfo(response));
        })
    }

    const dispatchFileBrowserList = () =>{
        FileHandler.getConnections(`${Env?.REACT_APP_PLATFORM_URL}/filebrowser/api`, (response) => {
            dispatch(setFileBrowserConnectionList(response));
        })
    }
    const dispatchWorkspaceList = () =>{
        WorkSpaceHandler.GetWorkSpaceList(`${Env.REACT_APP_PLATFORM_URL}/databricks/api`,(response) => {
            dispatch(setWorkspaceList(response));
        })
    }
    
    useEffect(() => {
        if (Env?.REACT_APP_DATABRICKS) {
            // Dispatch other actions immediately
            dispatchAllClusters();
    
            // Set up periodic dispatch for clusters
            const clusterTimer: NodeJS.Timeout | null = setInterval(() => {
                dispatchAllClusters();
            }, TIMER_GET_CLUSTERS);
            // Check for the presence of REACT_APP_PLATFORM_URL
            let platformUrlTimer: NodeJS.Timeout | null = null;
            if (!Env?.REACT_APP_PLATFORM_URL) {
                platformUrlTimer = setInterval(() => {
                    if (Env?.REACT_APP_PLATFORM_URL) {
                        dispatchUserActiveSessions();
                        dispatchFileBrowserList();
                        dispatchWorkspaceList();
                        // dispatch(getDataBricksVersionList());
                        clearInterval(platformUrlTimer!); // Clear the timer when condition is met
                    }
                }, 5000); // Poll every 5 seconds
            } else {
                dispatchUserActiveSessions(); // Dispatch immediately if already present
                dispatchFileBrowserList();
                dispatchWorkspaceList();
                // dispatch(getDataBricksVersionList());
            }
    
            return () => {
                // Clear timers on cleanup
                clusterTimer && clearInterval(clusterTimer);
                platformUrlTimer && clearInterval(platformUrlTimer);
            };
        }
    }, [Env?.REACT_APP_DATABRICKS]);
    

    useEffect(()=>{
        UserHandler.GetEnvVariables((res) => {
          dispatch(setEnvVariables(res)); 
        });
    },[])
    

    useDidUpdate(() => {
        history.push(studioTabs.tabs.visualizations.url);
    }, [goToAnalyticsPage]);


    useEffect(() => setActiveTabOnPageLoad(), [history.location.pathname]);

    const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        //  tabInfo: typeof activeTab
    ) => {
        if (!activeComponentPropertiesInfo.isSaved) {
            e.preventDefault();
            dispatch(closeActiveForm());
        } else if(!activeComponentPropertiesInfo.isNotesSaved) {
            e.preventDefault();
            dispatch(closeActiveComponentNotes())
        }
    };

    function renderTabs(category: Tabs) {
        return (
            <nav className="tabs__box">
                <span className="tabscategory__name">{category.name}</span>
                {Object.entries(category.tabs).map(([tabKey, tabInfo]) => {
                    const Icon = TabIcons[(tabKey as keyof typeof TabIcons)];
                    const isTabActive = activeTab.url === tabInfo.url;
                    const isFaTools = Icon === FaTools;
                    return (
                        <div key={tabInfo.name} className="tab__item__outer" id={`tab_${tabInfo.name.replace(" ", "_")}`}>
                            <TooltipRight key={tabInfo.name} title={tabInfo.name} mouseEnterDelay={1}>
                                <Link
                                    key={tabInfo.name}
                                    className={`tab__item ${isTabActive ? 'tab__item--active' : ''}`}
                                    onClick={(e: any) => handleTabClick(e,
                                        //  tabInfo
                                    )}
                                    to={tabInfo.url}
                                >
                                     {isFaTools ? (
                                    <FaTools size={25} color={isTabActive ? "#FCAD01" : "gray"} />
                                ) : (
                                    <Icon active={isTabActive} />
                                )}
                                </Link>
                            </TooltipRight>

                        </div>
                    );
                })
                }
            </nav>
        );
    }

    const isHamburgerEnabled = useMemo(() => (
        history.location.pathname.includes(studioTabs.tabs.visualizations.url) || 
        history.location.pathname.includes(dataTabsConfig.tabs.browser.url) || 
        history.location.pathname.includes(adminTabs.tabs.admin.url) ||
        history.location.pathname.includes(studioTabs.tabs.canvas.url) ||
        history.location.pathname.includes(otherTabs.tabs.others.url)
    ), [history.location.pathname]);


    const handleHamburgerClick = () => {
        if(isHamburgerEnabled) {
            const pathname = window.location.pathname;
            if (pathname.includes(studioTabs.tabs.visualizations.url)) dispatch(toggleWorkflowsInAnalytics());
            else if (pathname.includes(dataTabsConfig.tabs.browser.url)) dispatch(toggleConnectionsTreeview());
            else if (pathname.includes(adminTabs.tabs.admin.url)) dispatch(toggleAdministratorTreeview());
            else if (pathname.includes(otherTabs.tabs.others.url)) dispatch(toggleAdministratorTreeview());
            else dispatch(toggleTreeview());
        }
    };

    if (isDuplicateInstance) {
        return null;
    }
    if(isDrillingDataExplorer){
        return null;
    }

    return (
        <>
            <div 
                className={classNames('fixed-sidebar fixed-sidebar--left', { showSkeleton: showInitialPageLoadSpinner }, { hide: location.pathname === '/blank' && !showInitialPageLoadSpinner})}
                onClick={()=>dispatch(setPathInfo([{id:'default', name:'default'}]))}
            >
                <div
                    id='mini_title'
                >
                    <img src="/icons/sidebar/logo-DS.png" alt="" />
                </div>
                <div 
                    className={classNames('hamburger__icon', {'disabled': !isHamburgerEnabled})} 
                    id='hamburger__icon' 
                    onClick={handleHamburgerClick}
                >
                    <Hambuger className={classNames('reverse__img', isComponentTreeviewMinimized)} />
                </div>
                {renderTabs(studioTabs)}

                {renderTabs(dataTabsConfig)}
                {renderTabs(jobsTabsConfig)}
                {renderTabs(otherTabs)}
                {userInfo.isAdmin ?
                        <>
                     {renderTabs(adminTabs)}
                    </>
                    :
                    <img className="productOfDeepiQ__img" src="/icons/sidebar/logo-DEEPIQ.png" alt="" />
                }
                
                
            </div>
            <NetworkConnectionError />
            <ShowWhenTrue show={showInitialPageLoadSpinner}>
                <MainLoadingSkeleton />
            </ShowWhenTrue>
        </>
    );
};